import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ServiceCount as ServiceCountType } from 'src/models/package/_types';
import {
  fetchVisitIntervals,
  hasIntervalLimit,
} from 'src/models/user-package/utils';
import { Identifier } from 'src/types/utilityTypes';

import { Stack } from '../components/Stack';
import { Typography } from '../components/Typography';

import { VisitIntervalsButton } from './VisitIntervalsDialog';

const INTERVAL_LABELS: Record<string, { one: string; other: string }> = {
  day: { one: 'day', other: 'days' },
  week: { one: 'week', other: 'weeks' },
  month: { one: 'month', other: 'months' },
  year: { one: 'year', other: 'years' },
};

interface ServiceCountProps {
  count: ServiceCountType;
  isTrainerView?: boolean;
  userPackageId?: Identifier;
  serviceCountIndex?: number;
}

export const ServiceCount = ({
  count,
  isTrainerView = true,
  userPackageId,
  serviceCountIndex = 0,
}: ServiceCountProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.labels' });
  const { t: tPkg } = useTranslation('translation', {
    keyPrefix: 'packages.editPackageDialog',
  });

  const isInterval = hasIntervalLimit(count);
  const isFollowPlan = count.cadence === 'follow_plan';

  const { data: intervals } = useQuery({
    queryKey: ['visit-intervals', userPackageId],
    queryFn: () => fetchVisitIntervals(userPackageId!),
    enabled: isInterval && userPackageId != null,
    staleTime: 30000,
  });

  const currentInterval = intervals
    ?.filter((i) => i.serviceCountIndex === serviceCountIndex)
    ?.find((i) => i.isCurrent);

  const notIncludingReserved = !isTrainerView && (
    <Typography variant="body2" color="text.disabled" component="span">
      {' '}({tPkg('notIncludingReserved')})
    </Typography>
  );

  if (!isInterval) {
    const total = isFollowPlan ? count.maxVisits : count.count;
    return (
      <Typography variant="body2">
        {tPkg('usedOfLimit', { used: count.used, total: total || t('Unlimited').toLowerCase() })}
        {notIncludingReserved}
      </Typography>
    );
  }

  const intervalUnit = INTERVAL_LABELS[count.visitsInterval];
  const intervalLabel = intervalUnit
    ? (count.visitsIntervalCount === 1 ? intervalUnit.one : intervalUnit.other)
    : count.visitsInterval;
  const totalLimit = count.maxVisits;
  const intervalUsed = currentInterval?.used ?? 0;
  const intervalRemaining = currentInterval?.remaining ?? totalLimit;

  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">
        {tPkg('usedThisPeriod', { used: intervalUsed })}
        {notIncludingReserved}
      </Typography>
      {isTrainerView && (
        <Typography variant="body2">
          {tPkg('remainingResetsInterval', {
            remaining: intervalRemaining,
            total: totalLimit,
            count: count.visitsIntervalCount,
            interval: intervalLabel,
          })}
        </Typography>
      )}
      {userPackageId != null && (
        <VisitIntervalsButton
          userPackageId={userPackageId}
          serviceCountIndex={serviceCountIndex}
          variant="text"
        />
      )}
      {count.rolloverVisits > 0 && (
        <Typography variant="body2">
          <Typography variant="body2" component="span" color="text.secondary">
            {t('Rollover')}:
          </Typography>{' '}
          {count.rolloverVisits}
        </Typography>
      )}
    </Stack>
  );
};
